import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Button,
  Badge,
  Flex,
  Avatar,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Divider,
  List,
  ListItem,
  ListIcon,
  Tag,
} from "@chakra-ui/react";
import { FaArrowRight, FaCheckCircle, FaPhone, FaEnvelope, FaLinkedin, FaMapMarkerAlt } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const PROPERTY_PRIMARY = "#0D7F6E";
const NAVY = "#141C49";

type TeamGroup = "strategist" | "broker";

interface TeamMember {
  id: string;
  name: string;
  role: string;
  group: TeamGroup;
  tagline: string;
  location: string;
  bio: string;
  specialties: string[];
  credentials: string[];
  yearsExperience: number;
  photo?: string;
  portfoliosSeen?: string;
  portfoliosBuilt?: number;
  avgEquityGain?: number; // AUD
  phone: string;
  email: string;
}

const TEAM: TeamMember[] = [
  {
    id: "mark-mcevoy",
    name: "Mark McEvoy",
    role: "Senior Property Strategist",
    group: "strategist",
    tagline: "Building wealth through strategic acquisition and tax optimisation.",
    location: "Brisbane, QLD",
    bio: "Mark leads property strategy for high-income earners looking to build meaningful wealth outside of superannuation. With a background in financial planning and residential investment, he specialises in structuring portfolios that deliver strong rental yield alongside maximum depreciation and tax benefit. Mark has guided clients through acquisitions across South East Queensland, Northern NSW, and metro Melbourne.",
    specialties: ["Portfolio structuring", "Negative gearing strategies", "SMSF property acquisition", "Solar value-add advisory", "Dual-income household strategy"],
    credentials: ["Cert IV in Property Services", "Diploma of Financial Planning", "One Group Certified Property Strategist"],
    yearsExperience: 11,
    portfoliosSeen: "$42M+",
    portfoliosBuilt: 64,
    avgEquityGain: 187000,
    phone: "0400 000 001",
    email: "mark.mcevoy@onegroup.com.au",
  },
  {
    id: "ryan-lee",
    name: "Ryan Lee",
    role: "Property Investment Strategist",
    group: "strategist",
    tagline: "Data-driven decisions that take the guesswork out of investment property.",
    location: "Gold Coast, QLD",
    bio: "Ryan combines deep data analysis with on-the-ground market knowledge to pinpoint investment opportunities before they peak. He focuses on growth corridor identification, rental demand forecasting, and solar value-add timing to maximise total return for clients. Ryan works closely with the finance team to ensure every acquisition is structured for long-term serviceability.",
    specialties: ["Growth corridor analysis", "Rental demand forecasting", "Buy-and-hold strategy", "Solar ROI integration", "First investment property coaching"],
    credentials: ["Bachelor of Property Economics", "Cert IV in Property Services", "One Group Certified Property Strategist"],
    yearsExperience: 8,
    portfoliosSeen: "$28M+",
    portfoliosBuilt: 41,
    avgEquityGain: 142000,
    phone: "0400 000 002",
    email: "ryan.lee@onegroup.com.au",
  },
  {
    id: "chris-spelson",
    name: "Chris Spelson",
    role: "Property Strategist – Equity & Refinance",
    group: "strategist",
    tagline: "Unlocking dormant equity to fund the next stage of your portfolio.",
    location: "Sydney, NSW",
    bio: "Chris specialises in helping existing homeowners and investors identify and release equity to fund their next purchase — without disrupting their current position. He works closely with the finance brokers to structure refinances that open new borrowing capacity while maintaining a comfortable buffer. Chris has particular experience with clients making the transition from first home to investment portfolio.",
    specialties: ["Equity release strategies", "Refinance structuring", "First home to investment conversion", "Portfolio staging", "Cross-collateralisation risk management"],
    credentials: ["Cert IV in Property Services", "Diploma of Finance & Mortgage Broking", "One Group Certified Property Strategist"],
    yearsExperience: 9,
    portfoliosSeen: "$35M+",
    portfoliosBuilt: 53,
    avgEquityGain: 165000,
    phone: "0400 000 003",
    email: "chris.spelson@onegroup.com.au",
  },
  {
    id: "andrew-majdlik",
    name: "Andrew Majdlik",
    role: "Property Strategist – Commercial & SMSF",
    group: "strategist",
    tagline: "Commercial property and SMSF strategies that build retirement-grade wealth.",
    location: "Melbourne, VIC",
    bio: "Andrew focuses on commercial property investment and SMSF-held acquisitions for clients in the mid-to-late career stage looking to accelerate their super balance. He has deep expertise in limited recourse borrowing arrangements (LRBAs), commercial lease structuring, and the integration of solar assets within SMSF-compliant environments. Andrew coordinates with the finance team to ensure LRBA facilities are correctly structured from day one.",
    specialties: ["SMSF property acquisition", "Limited recourse borrowing (LRBA)", "Commercial lease structuring", "Industrial and retail investment", "Solar asset integration within SMSF"],
    credentials: ["Bachelor of Commerce (Finance)", "Cert IV in Property Services", "Registered Tax Agent (SMSF Planning)", "One Group Certified Property Strategist"],
    yearsExperience: 14,
    portfoliosSeen: "$61M+",
    portfoliosBuilt: 78,
    avgEquityGain: 224000,
    phone: "0400 000 004",
    email: "andrew.majdlik@onegroup.com.au",
  },
  {
    id: "neil-higgins",
    name: "Neil Higgins",
    role: "Finance Director",
    group: "broker",
    tagline: "Structuring finance that supports your portfolio for the long run.",
    location: "Brisbane, QLD",
    bio: "Neil brings some 35+ years in mortgage broking, having worked with Westpac as a broker BDM and headed Broker and Insurance Risk operations at a national company level. He has also operated his own business, and now serves as Finance Director for the past 2 years of operation of OFA.",
    specialties: ["Residential investment lending", "Commercial property finance", "SMSF / LRBA facilities", "Debt consolidation for investors", "Complex serviceability solutions"],
    credentials: ["Diploma of Finance and Mortgage Broking Management – AAMC", "MFAA – AML and CTF", "Commissioner for Declarations", "Responsible Manager – ACL Broking"],
    yearsExperience: 35,
    photo: "/assets/finance-assets/staff/127.jpg",
    phone: "0400 000 005",
    email: "neil.higgins@onegroup.com.au",
  },
  {
    id: "stephen-vider",
    name: "Stephen Vider",
    role: "Finance Broker – Investment & Refinance",
    group: "broker",
    tagline: "Making your borrowing capacity work as hard as your investment strategy.",
    location: "Gold Coast, QLD",
    bio: "Stephen specialises in investment property lending and refinance solutions for clients looking to release equity, consolidate debt, or fund a staged acquisition strategy. He has a strong background in working with dual-income households and high-income earners navigating complex lending policy. Stephen maintains close relationships with specialist lenders offering non-standard solutions for investors who don't fit the standard mould.",
    specialties: ["Investment property lending", "Equity release and refinance", "Dual-income borrower structuring", "Non-bank and specialist lenders", "Portfolio loan structuring"],
    credentials: ["Diploma of Finance & Mortgage Broking Management", "Certificate IV in Finance & Mortgage Broking", "FBAA Member"],
    yearsExperience: 10,
    photo: "/assets/finance-assets/staff/128.jpg",
    phone: "0400 000 006",
    email: "stephen.vider@onegroup.com.au",
  },
  {
    id: "helen-petterson",
    name: "Helen Petterson",
    role: "Finance Broker",
    group: "broker",
    tagline: "Helping first-time buyers and solar investors find the right finance from day one.",
    location: "Brisbane, QLD",
    bio: "With a zest for life at 57, Helen is married and proud mum of two. “I call the vibrant community of Banyo home, soaking in the Queensland sunshine for the past six years. My passion for helping people and becoming the best mortgage broker I can be, has inspired me to be a habitual learner with a focus on continuing professional development. I'm also registered with ASIC and a member of the FBAA, Toastmasters, and the Brisbane North Chamber of Commerce.”\n\nProfessional Journey:\n\nFrom a solid foundation of 8 years in banking and lending at a major bank, and starting as a mortgage broker in 2022, Helen is armed with the expertise to navigate the financial landscape with finesse. \"I'm not just your average mortgage broker—I'm your dedicated ally on the path to home ownership. With a passion for people and a background in customer-centric industries, I bring more than just expertise to the table. I'm here to listen, understand, and tailor solutions to your unique needs. Your journey isn't just about numbers and paperwork; it's about you and your dreams. Trust me to go above and beyond, ensuring that every step of the process ultimately leads you to achieving your goals. Whether it’s buying your family home, creating wealth with investment properties, or commercial lending - with me by your side, you're not just a client; your success is my top priority.\"\n\nPassions Beyond the Numbers:\n\nHelen makes every moment count: “When I'm not crunching mortgage figures, you'll find me immersing myself in the arts, painting vibrant landscapes, creating mosaic masterpieces, or transforming building items into works of art. My love for ballroom dancing once took me to competitions until a hip injury intervened, but that hasn't stopped me from enjoying life. I'm also a proud supporter of the Royal Australian Air Force Cadets and a seasoned camper who revels in outdoor concerts like the legendary Big Red Bash.”",
    specialties: [],
    credentials: ["Certificate IV in Finance and Mortgage Broking"],
    yearsExperience: 4,
    photo: "/assets/finance-assets/staff/126.jpg",
    phone: "0400 000 007",
    email: "helen.petterson@onegroup.com.au",
  },
];

const avatarColors: Record<TeamGroup, string> = {
  strategist: NAVY,
  broker: NAVY,
};

const groupLabels: Record<TeamGroup, string> = {
  strategist: "Property Strategists",
  broker: "Finance Brokers",
};

const TeamCard: React.FC<{ member: TeamMember; onOpen: (m: TeamMember) => void }> = ({ member, onOpen }) => (
  <Box
    bg="white"
    borderRadius="2xl"
    boxShadow="md"
    overflow="hidden"
    border="1px solid"
    borderColor="gray.100"
    transition="all 0.3s"
    _hover={{ boxShadow: "2xl", transform: "translateY(-4px)", borderColor: PROPERTY_PRIMARY }}
    display="flex"
    flexDirection="column"
  >
    <Box bg="blue.50" py={8} display="flex" justifyContent="center">
      <Avatar
        size="xl"
        name={member.name}
        src={member.photo}
        bg={avatarColors[member.group]}
        color="white"
        fontWeight="bold"
        fontSize="2xl"
      />
    </Box>
    <VStack spacing={2} p={6} flex={1} align="start">
      <Badge
        colorScheme="blue"
        borderRadius="full"
        px={3}
        py={1}
        fontSize="xs"
        textTransform="uppercase"
        letterSpacing="wider"
      >
        {member.group === "strategist" ? "Strategist" : "Finance Broker"}
      </Badge>
      <Heading size="md" color={NAVY} lineHeight="1.3">{member.name}</Heading>
      <Text fontSize="sm" color={PROPERTY_PRIMARY} fontWeight="600">{member.role}</Text>
      <HStack spacing={1} color="gray.500">
        <FaMapMarkerAlt size={12} />
        <Text fontSize="xs">{member.location}</Text>
      </HStack>
      <Text fontSize="sm" color="gray.600" pt={1} lineHeight="1.6" flex={1}>
        {member.tagline}
      </Text>
      <HStack spacing={2} pt={2} flexWrap="wrap">
        {member.group === "strategist" ? (
          <>
            {member.portfoliosBuilt && (
              <Tag size="sm" bg="gray.200" color="gray.700" fontWeight="600">{member.portfoliosBuilt}+ portfolios built</Tag>
            )}
            {member.avgEquityGain && (
              <Tag size="sm" bg={PROPERTY_PRIMARY} color="white" fontWeight="600">${Math.round(member.avgEquityGain / 1000)}k avg equity gain</Tag>
            )}
          </>
        ) : (
          <Tag size="sm" bg="gray.200" color="gray.700" fontWeight="600">{member.yearsExperience} yrs exp</Tag>
        )}
        {member.portfoliosSeen && <Tag size="sm" bg={PROPERTY_PRIMARY} color="white" fontWeight="600">{member.portfoliosSeen} managed</Tag>}
      </HStack>
    </VStack>
    <Box px={6} pb={6}>
      <Button
        w="full"
        size="sm"
        bg={PROPERTY_PRIMARY}
        color="white"
        _hover={{ bg: "#0a6459" }}
        rightIcon={<FaArrowRight />}
        onClick={() => onOpen(member)}
        borderRadius="lg"
      >
        View Profile
      </Button>
    </Box>
  </Box>
);

const PropertyTeam: React.FC = () => {
  const [selected, setSelected] = useState<TeamMember | null>(null);

  const strategists = TEAM.filter((m) => m.group === "strategist");
  const brokers = TEAM.filter((m) => m.group === "broker");

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/team"
        fallbackTitle="Meet the Team | One Property Australia"
        fallbackDescription="Our property strategists and finance brokers work together to build your investment portfolio with data-driven precision."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box
        position="relative"
        color="white"
        py={{ base: 16, md: 24 }}
        backgroundImage="url('https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1920&q=80')"
        backgroundSize="cover"
        backgroundPosition="center top"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(20, 20, 20, 0.55)",
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Badge bg={PROPERTY_PRIMARY} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider" boxShadow="0 2px 8px rgba(13, 127, 110, 0.4)">
              Our Team
            </Badge>
            <Heading size="3xl" fontWeight="black" maxW="3xl" lineHeight="1.2">
              The People Behind Your Portfolio
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl" lineHeight="1.8">
              Our property strategists and finance brokers work in lockstep — every acquisition is built on the right strategy and the right structure from day one.
            </Text>
            <HStack spacing={4} pt={2} flexWrap="wrap" justify="center">
              <Button as={RouterLink} to="/property/consultation" size="lg" bg="white" color={NAVY} _hover={{ bg: "#0a6459", color: "white" }} rightIcon={<FaArrowRight />} px={8} transition="all 0.2s">
                Book a Consultation
              </Button>
              <Button as={RouterLink} to="/property/contact" size="lg" variant="outline" borderColor="white" color="white" _hover={{ bg: "#0a6459", borderColor: "#0a6459" }} px={8} transition="all 0.2s">
                Get in Touch
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Stats bar — parallax image matching PropertyHome */}
      <Box
        position="relative"
        color="white"
        py={20}
        backgroundImage="url('https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1920&q=80')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(20, 20, 20, 0.60)",
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={8}>
            <Heading size="xl" textAlign="center" textShadow="0 2px 4px rgba(0,0,0,0.3)">
              A Team Built for Serious Investors
            </Heading>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8} w="full" pt={4}>
              {[
                { label: "Team Members", value: "7" },
                { label: "Combined Experience", value: "75+ yrs" },
                { label: "Portfolios Managed", value: "$160M+" },
                { label: "Lender Accreditations", value: "30+" },
              ].map((stat) => (
                <VStack key={stat.label} spacing={2}>
                  <Heading size="2xl" color="white" textShadow="0 2px 8px rgba(0,0,0,0.4)">{stat.value}</Heading>
                  <Text fontSize="md" color="rgba(255,255,255,0.9)" textShadow="0 1px 2px rgba(0,0,0,0.3)">{stat.label}</Text>
                </VStack>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Team sections */}
      <Box bg="gray.50">
      <Container maxW="container.xl" py={16}>
        {/* Property Strategists */}
        <VStack spacing={10} align="stretch" mb={20}>
          <Box>
            <HStack spacing={3} mb={2}>
              <Box w={1} h={8} bg={NAVY} borderRadius="full" />
              <Heading size="xl" color={NAVY}>{groupLabels.strategist}</Heading>
            </HStack>
            <Text color="gray.600" maxW="2xl">
              Our strategists identify the right properties, in the right markets, at the right time — then structure your acquisition for maximum tax efficiency and long-term growth.
            </Text>
          </Box>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6}>
            {strategists.map((m) => (
              <TeamCard key={m.id} member={m} onOpen={setSelected} />
            ))}
          </SimpleGrid>
        </VStack>

        <Divider mb={20} />

        {/* Finance Brokers */}
        <VStack spacing={10} align="stretch">
          <Box>
            <HStack spacing={3} mb={2}>
              <Box w={1} h={8} bg={PROPERTY_PRIMARY} borderRadius="full" />
              <Heading size="xl" color={NAVY}>{groupLabels.broker}</Heading>
            </HStack>
            <Text color="gray.600" maxW="2xl">
              Our brokers hold accreditations with 30+ lenders and work alongside the strategy team to ensure every deal is funded at the best rate, with the right structure, from the start.
            </Text>
          </Box>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6}>
            {brokers.map((m) => (
              <TeamCard key={m.id} member={m} onOpen={setSelected} />
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
      </Box>

      {/* CTA band — matching PropertyHome navy bottom */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.lg">
          <VStack spacing={6} textAlign="center">
            <Heading size="xl">Ready to Build Your Portfolio?</Heading>
            <Text fontSize="lg" color="gray.300" maxW="2xl">
              Book a no-obligation consultation and we'll match you with the right strategist and broker for your situation.
            </Text>
            <HStack spacing={4} pt={4} flexWrap="wrap" justify="center">
              <Button as={RouterLink} to="/property/consultation" bg="white" color={NAVY} _hover={{ bg: "#0a6459", color: "white" }} size="lg" rightIcon={<FaArrowRight />} transition="all 0.2s">
                Book Consultation
              </Button>
              <Button as={RouterLink} to="/property/contact" variant="outline" borderColor="white" color="white" _hover={{ bg: "#0a6459", borderColor: "#0a6459" }} size="lg" transition="all 0.2s">
                Get in Touch
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />

      {/* Profile Modal */}
      <Modal isOpen={!!selected} onClose={() => setSelected(null)} size="2xl" scrollBehavior="inside" motionPreset="slideInBottom">
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" overflow="hidden" mx={4} bg="white">
          {selected && (
            <>
              <Box bg={NAVY} py={8} px={8}>
                <HStack spacing={5} align="start">
                  <Avatar
                    size="xl"
                    name={selected.name}
                    src={selected.photo}
                    bg="whiteAlpha.300"
                    color="white"
                    fontWeight="bold"
                    fontSize="2xl"
                    flexShrink={0}
                  />
                  <VStack align="start" spacing={1}>
                    <Badge bg="whiteAlpha.300" color="white" px={3} py={1} borderRadius="full" fontSize="xs" textTransform="uppercase">
                      {selected.group === "strategist" ? "Property Strategist" : "Finance Broker"}
                    </Badge>
                    <Heading size="lg" color="white">{selected.name}</Heading>
                    <Text color="whiteAlpha.800" fontWeight="500">{selected.role}</Text>
                    <HStack spacing={1} color="whiteAlpha.700">
                      <FaMapMarkerAlt size={12} />
                      <Text fontSize="sm">{selected.location}</Text>
                    </HStack>
                  </VStack>
                </HStack>
              </Box>

              <ModalCloseButton color="white" top={4} right={4} />

              <ModalHeader display="none">{selected.name}</ModalHeader>

              <ModalBody py={8} px={8} bg="white">
                <VStack spacing={8} align="stretch">
                  {/* Bio */}
                  <Box>
                    <Text color="gray.700" lineHeight="1.8" fontSize="md" whiteSpace="pre-line">
                      {selected.bio}
                    </Text>
                  </Box>

                  {/* Stats */}
                  <SimpleGrid columns={selected.portfoliosSeen ? 3 : 2} spacing={4}>
                    <Box bg="gray.50" p={4} borderRadius="xl" textAlign="center">
                      <Text fontSize="2xl" fontWeight="black" color={NAVY}>
                        {selected.yearsExperience}+
                      </Text>
                      <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">Years Experience</Text>
                    </Box>
                    {selected.portfoliosSeen && (
                      <Box bg="gray.50" p={4} borderRadius="xl" textAlign="center">
                        <Text fontSize="2xl" fontWeight="black" color={NAVY}>
                          {selected.portfoliosSeen}
                        </Text>
                        <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">Portfolios Managed</Text>
                      </Box>
                    )}
                    <Box bg="gray.50" p={4} borderRadius="xl" textAlign="center">
                      <Text fontSize="2xl" fontWeight="black" color={NAVY}>
                        {selected.credentials.length}
                      </Text>
                      <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">Qualifications</Text>
                    </Box>
                  </SimpleGrid>

                  {/* Specialties */}
                  {selected.specialties.length > 0 && (
                    <Box>
                      <Heading size="sm" color={NAVY} mb={3}>Areas of Speciality</Heading>
                      <List spacing={2}>
                        {selected.specialties.map((s) => (
                          <ListItem key={s} display="flex" alignItems="center" gap={2}>
                            <ListIcon as={FaCheckCircle} color={PROPERTY_PRIMARY} mt="1px" flexShrink={0} />
                            <Text fontSize="sm" color="gray.700">{s}</Text>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Credentials */}
                  <Box>
                    <Heading size="sm" color={NAVY} mb={3}>Qualifications & Credentials</Heading>
                    <VStack align="start" spacing={2}>
                      {selected.credentials.map((c) => (
                        <Tag key={c} bg="gray.100" color={NAVY} fontWeight="600" borderRadius="full" px={3} py={1} fontSize="xs">
                          {c}
                        </Tag>
                      ))}
                    </VStack>
                  </Box>

                  <Divider />

                  {/* Contact */}
                  <Box>
                    <Heading size="sm" color={NAVY} mb={4}>Get in Touch</Heading>
                    <VStack spacing={3} align="stretch">
                      <HStack spacing={3} p={3} bg="gray.50" borderRadius="lg">
                        <FaPhone color={PROPERTY_PRIMARY} />
                        <Text fontSize="sm" color="gray.700">{selected.phone}</Text>
                        <Text fontSize="xs" color="gray.400">(placeholder)</Text>
                      </HStack>
                      <HStack spacing={3} p={3} bg="gray.50" borderRadius="lg">
                        <FaEnvelope color={PROPERTY_PRIMARY} />
                        <Text fontSize="sm" color="gray.700">{selected.email}</Text>
                        <Text fontSize="xs" color="gray.400">(placeholder)</Text>
                      </HStack>
                    </VStack>
                  </Box>

                  <HStack spacing={3} pt={2}>
                    <Button
                      as={RouterLink}
                      to="/property/consultation"
                      bg={selected.group === "strategist" ? PROPERTY_PRIMARY : NAVY}
                      color="white"
                      _hover={{ opacity: 0.88 }}
                      rightIcon={<FaArrowRight />}
                      flex={1}
                      onClick={() => setSelected(null)}
                    >
                      Book with {selected.name.split(" ")[0]}
                    </Button>
                    <Button
                      variant="outline"
                      borderColor="gray.300"
                      color="gray.600"
                      leftIcon={<FaLinkedin />}
                      flex={1}
                      isDisabled
                    >
                      LinkedIn (coming soon)
                    </Button>
                  </HStack>
                </VStack>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PropertyTeam;
